import { useCallback, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { showInkToast } from '../cms/InkToast';
import {
  APPLY_VOICE_REWRITES,
  RewriteResult,
  rewriteRefused,
  UNDO_VOICE_REWRITES,
  VoiceKind,
} from './voiceWords';

/**
 * Take the stored rewrite on one post or a whole card, with one Undo. Every
 * list on screen reads the new words and the new check afterwards.
 */
export function useVoiceRewrite(onDone?: () => unknown) {
  const client = useApolloClient();
  const [apply] = useMutation(APPLY_VOICE_REWRITES);
  const [undo] = useMutation(UNDO_VOICE_REWRITES);
  const [rewriting, setRewriting] = useState(false);

  const refresh = useCallback(async () => {
    await client.refetchQueries({ include: 'active' });
  }, [client]);

  const rewrite = useCallback(
    async (items: Array<{ kind: VoiceKind; id: string }>) => {
      if (!items.length) return false;
      setRewriting(true);
      try {
        const { data } = await apply({ variables: { items } });
        const results: RewriteResult[] = data?.applyVoiceRewrites || [];
        const done = results.filter((r) => r.applied);
        const refused = rewriteRefused(results);
        if (!done.length) {
          showInkToast(refused || 'There’s no rewrite for this yet. Try again in a moment.');
          return false;
        }
        await refresh();
        await onDone?.();
        showInkToast(
          refused ||
            (done.length === 1
              ? 'Rewritten in your voice.'
              : `${done.length} posts rewritten in your voice.`),
          {
            label: 'Undo',
            onClick: () => {
              void (async () => {
                try {
                  await undo({
                    variables: {
                      items: done.map((r) => ({
                        kind: r.kind,
                        id: r.id,
                        text: r.previousText || '',
                      })),
                    },
                  });
                  await refresh();
                  await onDone?.();
                } catch (err) {
                  showInkToast(err instanceof Error ? err.message : 'That didn’t work. Try again.');
                }
              })();
            },
          }
        );
        return true;
      } catch (err) {
        showInkToast(err instanceof Error ? err.message : 'That didn’t work. Try again.');
        return false;
      } finally {
        setRewriting(false);
      }
    },
    [apply, undo, refresh, onDone]
  );

  return { rewrite, rewriting };
}
